import RadioButtonField from "@features/onboarding/components/RadioButtonField";
import { useUpdateOnboardingUser } from "@features/onboarding/mutations/useUpdateOnboardingUser";
import { GenderEnumSchema } from "@fitties/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import logger from "@logger";
import Container from "@shared/components/Container";
import { useCurrentUser } from "@shared/queries";
import clsx from "clsx";
import { useForm } from "react-hook-form";
import { z } from "zod";

// Validation schema for gender selection
const genderSchema = z.object({
  gender: GenderEnumSchema,
});

type GenderFormData = z.infer<typeof genderSchema>;

// Type-safe gender options
type GenderOption = {
  value: z.infer<typeof GenderEnumSchema>;
  label: string;
};

const genderOptions: readonly GenderOption[] = [
  { value: "female", label: "Female" },
  { value: "male", label: "Male" },
  { value: "nonbinary", label: "Other" },
] as const;

/**
 * Gender selection form component for onboarding flow.
 *
 * Displays a purple background with back arrow, heading, and radio button options
 * for gender selection (Female, Male, Non-binary) with proper validation using
 * React Hook Form and Zod. Handles form submission automatically.
 *
 * @example
 * ```tsx
 * <GenderForm />
 * ```
 */
interface GenderFormProps {
  onSaved?: () => void;
}

export default function GenderForm({ onSaved }: GenderFormProps) {
  const { user } = useCurrentUser();
  const { mutate: updateOnboardingUser, isPending: isSubmitting, error: mutationError } = useUpdateOnboardingUser();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<GenderFormData>({
    resolver: zodResolver(genderSchema),
    ...(user?.gender ? { defaultValues: { gender: user.gender } } : {}),
    mode: "onChange",
  });

  const selectedGender = watch("gender");

  const handleFormSubmit = (data: GenderFormData) => {
    logger.info("📝 GenderForm submitting data:", data);
    updateOnboardingUser(data, {
      onSuccess: (result) => {
        logger.info("✅ GenderForm mutation successful:", result);
        onSaved?.();
      },
      onError: (error) => {
        logger.error("❌ GenderForm mutation failed:", error);
      },
    });
  };

  return (
    <div data-testid="gender-form-root" className="h-full min-h-0 flex flex-col text-center font-mono bg-lavender-400">
      {/* Content */}
      <div data-testid="gender-form-content" className="flex-1 overflow-y-auto space-y-4 pb-2">
        {/* Title */}
        <h1 data-testid="gender-form-heading" className="text-2xl font-bold text-black">
          What's your gender?
        </h1>

        {/* Description */}
        <p data-testid="gender-form-description" className="text-black/80 text-md">
          Pick what describes you best
        </p>

        {/* Form */}
        <form
          data-testid="gender-form"
          onSubmit={handleSubmit(handleFormSubmit)}
          className="mx-auto w-full space-y-4"
          noValidate
        >
          {/* Server Error Message */}
          {mutationError && (
            <div data-testid="gender-form-error" className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p data-testid="gender-form-error-message" className="text-red-600 text-sm">
                {mutationError.message}
              </p>
            </div>
          )}

          {/* Gender Options */}
          <div data-testid="gender-form-options" className="space-y-2">
            {genderOptions.map((option) => (
              <Container key={option.value}>
                <RadioButtonField
                  {...register("gender")}
                  value={option.value}
                  label={option.label}
                  formValue={selectedGender}
                  {...(errors.gender?.message && { error: errors.gender.message })}
                />
              </Container>
            ))}
          </div>
        </form>
      </div>

      {/* Button at bottom */}
      <div data-testid="gender-form-footer" className="sticky bottom-0 z-10 bg-lavender-400 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          onClick={handleSubmit(handleFormSubmit)}
          className={clsx("w-full px-8 py-4 text-lg rounded-lg font-medium transition-colors font-sans", {
            "bg-pink-600 text-white hover:bg-pink-700": !isSubmitting,
            "bg-gray-300 text-gray-500 cursor-not-allowed": isSubmitting,
          })}
          data-testid="submit-button"
        >
          <span data-testid="gender-form-submit-label">{isSubmitting ? "Saving..." : "Continue"}</span>
        </button>
      </div>
    </div>
  );
}
